import { DialogComponent } from '@theme/dialog';

/**
 * Login / register authentication modal.
 *
 * Extends the theme's DialogComponent so it reuses modal open/close, scroll lock,
 * outside-click and Escape handling. Adds tab switching between the login and
 * register panels, and opens from any `[data-pet-auth-open]` trigger in the page.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} panelLogin - The login tab panel.
 * @property {HTMLElement} panelRegister - The register tab panel.
 * @property {HTMLButtonElement} tabLogin - The login tab button.
 * @property {HTMLButtonElement} tabRegister - The register tab button.
 * @property {HTMLElement} title - The modal heading.
 * @property {HTMLElement} subtitle - The modal subheading.
 * @property {HTMLElement} switchText - The footer prompt text.
 * @property {HTMLButtonElement} switchButton - The footer toggle button.
 *
 * @extends {DialogComponent}
 */
class PetAuthModal extends DialogComponent {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {'login' | 'register'} */
  #mode = 'login';

  #copy = {
    login: {
      title: 'Bienvenido de vuelta',
      subtitle: 'Inicia sesión para completar tu compra.',
      switchText: '¿Aún no tienes cuenta?',
      switchButton: 'Regístrate',
    },
    register: {
      title: 'Crea tu cuenta',
      subtitle: 'Regístrate y recibe 10% de descuento automático en tu primera compra.',
      switchText: '¿Ya tienes cuenta?',
      switchButton: 'Inicia sesión',
    },
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    document.addEventListener('click', this.#handleOpenerClick, { signal: this.#controller.signal });
    this.#bindRegisterNote();
    this.#restoreAfterSubmit();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /**
   * Packs ciudad, mascota and consent into the customer note Shopify stores.
   */
  #bindRegisterNote() {
    const form = this.refs.panelRegister?.querySelector('form');
    if (!form) return;

    form.addEventListener(
      'submit',
      () => {
        const note = form.querySelector('[data-pet-customer-note]');
        if (!(note instanceof HTMLInputElement)) return;

        const lines = [];
        for (const field of form.querySelectorAll('[data-pet-note]')) {
          if (!(field instanceof HTMLInputElement)) continue;
          if (field.type === 'checkbox' && !field.checked) continue;
          if (field.type === 'radio' && !field.checked) continue;
          const value = field.value.trim();
          if (!value) continue;
          lines.push(`${field.dataset.petNote}: ${value}`);
        }
        note.value = lines.join('\n');
      },
      { signal: this.#controller.signal }
    );
  }

  /** Reopens the matching panel after Shopify reloads with form errors. */
  #restoreAfterSubmit() {
    const registerHadErrors = Boolean(this.refs.panelRegister?.querySelector('.pet-auth__error'));
    const loginHadErrors = Boolean(this.refs.panelLogin?.querySelector('.pet-auth__error'));
    if (registerHadErrors) {
      this.setMode('register');
      this.showDialog();
    } else if (loginHadErrors) {
      this.setMode('login');
      this.showDialog();
    }
  }

  /**
   * Opens the modal when a `[data-pet-auth-open]` trigger is clicked.
   * @param {MouseEvent} event
   */
  #handleOpenerClick = async (event) => {
    const element = event.target instanceof Element ? event.target : null;
    const trigger = element?.closest('[data-pet-auth-open]');
    if (!trigger) return;

    event.preventDefault();

    const drawer = trigger.closest('theme-drawer');
    if (drawer?.hasAttribute('open') && typeof drawer.close === 'function') {
      await drawer.close();
    }

    const requested = trigger.getAttribute('data-pet-auth-open');
    this.setMode(requested === 'register' ? 'register' : 'login');
    this.showDialog();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = this.#mode === 'login' ? this.refs.panelLogin : this.refs.panelRegister;
        const firstInput = panel?.querySelector('input');
        if (firstInput instanceof HTMLInputElement) firstInput.focus();
      });
    });
  };

  /** Shows the login panel. */
  showLogin = () => this.setMode('login');

  /** Shows the register panel. */
  showRegister = () => this.setMode('register');

  /** Toggles between login and register. */
  toggleMode = () => this.setMode(this.#mode === 'login' ? 'register' : 'login');

  /**
   * Switches the active panel and keeps tabs, copy and focus in sync.
   * @param {'login' | 'register'} mode
   */
  setMode(mode) {
    this.#mode = mode;
    const isLogin = mode === 'login';
    const { panelLogin, panelRegister, tabLogin, tabRegister, title, subtitle, switchText, switchButton } = this.refs;

    if (panelLogin) panelLogin.hidden = !isLogin;
    if (panelRegister) panelRegister.hidden = isLogin;

    this.#syncTab(tabLogin, isLogin);
    this.#syncTab(tabRegister, !isLogin);

    const copy = this.#copy[mode];
    if (title) title.textContent = copy.title;
    if (subtitle) subtitle.textContent = copy.subtitle;
    if (switchText) switchText.textContent = copy.switchText;
    if (switchButton) switchButton.textContent = copy.switchButton;

    const activePanel = isLogin ? panelLogin : panelRegister;
    const firstInput = activePanel?.querySelector('input');
    if (firstInput instanceof HTMLInputElement && this.refs.dialog.open) {
      requestAnimationFrame(() => firstInput.focus());
    }
  }

  /**
   * @param {HTMLButtonElement | undefined} tab
   * @param {boolean} active
   */
  #syncTab(tab, active) {
    if (!tab) return;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
  }
}

if (!customElements.get('pet-auth-modal')) {
  customElements.define('pet-auth-modal', PetAuthModal);
}
